import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import ChatList from './ChatList';
import ChatDetails from './ChatDetails';
import ChatStatistics from './ChatStatistics';
import SystemChatManagement from './SystemChatManagement';
import { getAdminChats, getAdminChatStatistics } from '../api/adminChats';

const DEFAULT_FILTERS = {
  search: '',
  status: '',
  date_from: '',
  date_to: '',
  sort_by: 'updated_at',
  sort_order: 'desc',
};

const TABS = [
  { id: 'list', name: 'Чаты', icon: ChatBubbleLeftRightIcon },
  { id: 'statistics', name: 'Статистика', icon: ChartBarIcon },
  { id: 'system-chats', name: 'Рассылка', icon: PaperAirplaneIcon },
];

const ChatManagement = () => {
  const [activeView, setActiveView] = useState('list');
  const [selectedChat, setSelectedChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const requestId = useRef(0);

  const fetchChats = useCallback(async (page = 1, activeFilters = DEFAULT_FILTERS) => {
    // Игнорируем устаревшие ответы (быстрый ввод в поиске)
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminChats({ page, limit: 20, ...activeFilters });
      if (id !== requestId.current) return;
      setChats(data.items || []);
      setPagination({
        page: data.page || page,
        limit: data.size || 20,
        total: data.total || 0,
        pages: data.pages || 0,
      });
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.message);
      console.error('Error fetching chats:', err);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  const fetchStatistics = useCallback(async (period) => {
    try {
      const data = await getAdminChatStatistics({ period });
      setStatistics(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  }, []);

  useEffect(() => {
    fetchStatistics(statsPeriod);
  }, [fetchStatistics, statsPeriod]);

  // Поиск — с задержкой, остальные фильтры — сразу
  useEffect(() => {
    const t = setTimeout(() => fetchChats(1, filters), filters.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [filters, fetchChats]);

  const handleFilterChange = (newFilters) => setFilters(newFilters);
  const handlePageChange = (page) => fetchChats(page, filters);

  const handleChatSelect = (chat) => {
    setSelectedChat(chat);
    setActiveView('details');
  };

  const handleBackToList = () => {
    setActiveView('list');
    setSelectedChat(null);
  };

  const handleRefresh = () => {
    fetchChats(pagination.page, filters);
    // Карточкалар (Активных / Удалённых) статистикадан алынады — өшіргеннен кейін де жаңарсын.
    fetchStatistics(statsPeriod);
  };

  const currentTab = activeView === 'details' ? 'list' : activeView;

  return (
    <div className="space-y-4 text-left">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Чаты</h2>
          <p className="mt-0.5 text-sm text-text-secondary">Переписка пользователей, статистика и системные рассылки</p>
        </div>
        <div role="tablist" className="flex rounded-lg border border-border-primary bg-bg-card p-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = currentTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setActiveView(tab.id);
                  if (tab.id !== 'list') setSelectedChat(null);
                }}
                className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  active ? 'bg-accent-grad text-white shadow-sm' : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.name}
              </button>
            );
          })}
        </div>
      </div>

      {activeView === 'list' && (
        <ChatList
          chats={chats}
          statistics={statistics}
          pagination={pagination}
          filters={filters}
          loading={loading}
          error={error}
          onChatSelect={handleChatSelect}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          onRefresh={handleRefresh}
        />
      )}

      {activeView === 'details' && selectedChat && (
        <ChatDetails chat={selectedChat} onBack={handleBackToList} onRefresh={handleRefresh} />
      )}

      {activeView === 'statistics' && (
        <ChatStatistics
          statistics={statistics}
          period={statsPeriod}
          onPeriodChange={setStatsPeriod}
          onRefresh={() => fetchStatistics(statsPeriod)}
        />
      )}

      {activeView === 'system-chats' && <SystemChatManagement />}
    </div>
  );
};

export default ChatManagement;
